import { Box, Button, HStack, Heading, IconButton, Text } from "@chakra-ui/react";
import { ArrowLeft } from "@phosphor-icons/react";
import PageHeader from "components/text/PageHeader";
import { prettyPrintCodeName } from "utils/text-utils";

/**
 * Wrapper for a step in a wizard. Provides a header and next/previous buttons.
 * Place the forms (or other content) in as the child element (and manage what to show via stepIndex).
 * Use-case: to help with consistent styling of wizard steps.
 *
 */
export default function WizardStepWrapper({
  children,
  nextText = "Next",
  previousText = "Previous",
  gotoNext,
  gotoPrevious,
  onCancel,
  stepIndex,
  steps,
  superTitle,
  title,
  subTitle,
  nextDisabled,
}) {
  let displayTitle = title;
  if (displayTitle === undefined || displayTitle === null) {
    // false is allowed for no title
    displayTitle = prettyPrintCodeName(steps[stepIndex]);
  }

  const backLabel = stepIndex > 0 ? prettyPrintCodeName(steps[stepIndex - 1]) : "Back";
  const backAction = stepIndex > 0 ? gotoPrevious : onCancel;

  return (
    <Box display="flex" flexDirection="column" h="100%">
      {backAction && (
        <HStack spacing={2} mb={16} cursor="pointer" onClick={backAction} role="button" w="fit-content">
          <IconButton
            icon={<ArrowLeft size={16} />}
            aria-label="Go back"
            variant="ghost"
            size="xs"
            color="limeShade.600"
            minW="auto"
            as="span"
          />
          <Text color="limeShade.600" fontSize="body.m">
            {backLabel}
          </Text>
        </HStack>
      )}
      {superTitle && (
        <Text color="limeShade.600" fontSize="body.m" mb={0}>
          {superTitle}
          {steps && ` (Step ${stepIndex + 1} of ${steps.length})`}
        </Text>
      )}
      {(displayTitle || subTitle) && <PageHeader title={displayTitle} subTitle={subTitle} />}
      {children}
      <Box flexGrow={1} />
      <Box w="100%" mt={4}>
        {stepIndex < steps.length - 1 && gotoNext && (
          <Button
            variant="primary"
            w="100%"
            onClick={gotoNext}
            isDisabled={nextDisabled}
            title={typeof nextDisabled === "string" ? nextDisabled : undefined}
          >
            {nextText}
          </Button>
        )}
      </Box>
    </Box>
  );
}
